export type ThemePreference = 'light' | 'dark' | 'system'

export const THEME_STORAGE_KEY = 'lumora.theme'
export const THEME_VALUES: readonly ThemePreference[] = ['light', 'dark', 'system']

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (THEME_VALUES as readonly string[]).includes(value)
}

export function readStoredTheme(storage: Pick<Storage, 'getItem'> | null = safeStorage()): ThemePreference {
  try {
    const value = storage?.getItem(THEME_STORAGE_KEY)
    return isThemePreference(value) ? value : 'system'
  } catch {
    return 'system'
  }
}

export function storeTheme(preference: ThemePreference, storage: Pick<Storage, 'setItem'> | null = safeStorage()) {
  try {
    storage?.setItem(THEME_STORAGE_KEY, preference)
  } catch {
    /* storage unavailable (private mode): theme still applies for this session */
  }
}

export function resolveTheme(preference: ThemePreference, systemPrefersDark: boolean): 'light' | 'dark' {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light'
  return preference
}

export function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false
}

/** Applies the resolved theme to <html>. Returns what was applied. */
export function applyTheme(preference: ThemePreference, root: HTMLElement = document.documentElement) {
  const resolved = resolveTheme(preference, systemPrefersDark())
  root.classList.toggle('dark', resolved === 'dark')
  root.dataset.theme = resolved
  return resolved
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}
